<template>
  <div class="pos-ticket-page">
    <div class="pos-ticket-toolbar no-print">
      <button type="button" class="btn-ticket btn-print" @click="printTicket">
        <i class="fas fa-print me-1"></i> Imprimir
      </button>
      <button type="button" class="btn-ticket btn-close-tab" @click="closeTicket">
        <i class="fas fa-times me-1"></i> Cerrar
      </button>
    </div>

    <div v-if="loading" class="pos-ticket-empty no-print">
      <p>Cargando ticket...</p>
    </div>

    <div v-else-if="!ticket" class="pos-ticket-empty no-print">
      <p>No hay datos de ticket para mostrar.</p>
      <button type="button" class="btn-ticket btn-close-tab" @click="closeTicket">Cerrar</button>
    </div>

    <article v-else class="ticket" id="pos-ticket-print">
      <header class="ticket-header">
        <h1 class="ticket-company">{{ companyLine }}</h1>
        <p v-if="ticket.company?.nit" class="ticket-sub">NIT: {{ ticket.company.nit }}</p>
        <p class="ticket-sub">Tel.: {{ ticket.company?.phone || "—" }}</p>
      </header>

      <section class="ticket-doc" >
        <div class="ticket-sep" style="width: 100%;border-style: dashed;border-width: 0.2px;"></div>
        <div class="ticket-doc-title">Orden de pedido</div>
        <div class="ticket-doc-row">
          <span class="ticket-doc-number">No. {{ ticket.invoiceNo }}</span>
          <span class="ticket-doc-date">{{ ticket.dateFormatted }}</span>
        </div>
      </section>

      <section class="ticket-customer">
        <div class="ticket-customer-name">{{ customerName }}</div>
        <div class="ticket-customer-doc">{{ documentLine }}</div>
      </section>

      <section class="ticket-items">
        <div class="ticket-items-head">
          <div class="th-num">#</div>
          <div class="th-qty">Cant.</div>
          <div class="th-desc">
            <span>Descripción</span>
            <span class="th-sub">Impuestos</span>
          </div>
          <div class="th-total">
            <span>Valor total</span>
            <span class="th-sub">Descuento</span>
          </div>
        </div>
        <div class="ticket-sep" style="width: 100%;border-style: dashed;border-width: 0.2px;"></div>
        <div
          v-for="(item, index) in ticket.items"
          :key="index"
          class="ticket-item-row"
        >
          <div class="td-num">{{ index + 1 }}</div>
          <div class="td-qty">{{ formatQty(item.quantity) }}</div>
          <div class="td-desc">{{ itemLabel(item) }}</div>
          <div class="td-total">{{ money(item.total) }}</div>
        </div>
      </section>

      <section class="ticket-totals">
        <div class="ticket-totals-block">
          <div v-if="ticket.discountAmount > 0" class="ticket-total-line">
            <span class="ticket-total-label">DESCUENTO ({{ Number(ticket.discountPercent).toFixed(2) }}%) ==&gt;</span>
            <span class="ticket-total-value">−{{ money(ticket.discountAmount) }}</span>
          </div>
          <div v-if="ticket.flete > 0" class="ticket-total-line">
            <span class="ticket-total-label">FLETE ==&gt;</span>
            <span class="ticket-total-value">+{{ money(ticket.flete) }}</span>
          </div>
          <div v-if="ticket.giftValue > 0" class="ticket-total-line">
            <span class="ticket-total-label">OBSEQUIO<span v-if="ticket.giftDescription"> ({{ ticket.giftDescription }})</span> ==&gt;</span>
            <span class="ticket-total-value">+{{ money(ticket.giftValue) }}</span>
          </div>
          <div class="ticket-total-line ticket-total-main">
            <span class="ticket-total-label">TOTAL ===========&gt;</span>
            <span class="ticket-total-value">{{ money(ticket.total) }}</span>
          </div>
          <div class="ticket-total-line">
            <span class="ticket-total-label">RECIBIDO ==&gt;</span>
            <span class="ticket-total-value">{{ money(ticket.received) }}</span>
          </div>
          <div class="ticket-total-line">
            <span class="ticket-total-label">CAMBIO ==&gt;</span>
            <span class="ticket-total-value">{{ money(ticket.change) }}</span>
          </div>
        </div>
      </section>

      <section class="ticket-payment">
        <div class="ticket-payment-head">
          <span>Forma de pago</span>
          <span>Valor</span>
        </div>
        <div class="ticket-payment-row">
          <span>{{ ticket.paymentMethod }}</span>
          <span>{{ money(ticket.received) }}</span>
        </div>
      </section>

      <section class="ticket-footer">
        <div class="ticket-seller">VENDEDOR</div>
        <div class="ticket-footer-block">
          <div class="ticket-footer-line">
            <span>TOTAL :</span>
            <span>{{ money(ticket.total) }}</span>
          </div>
          <div class="ticket-footer-line">
            <span>NRO. ARTICULOS VEND. :</span>
            <span>{{ ticket.articlesCount }}</span>
          </div>
          <div class="ticket-footer-line">
            <span>NRO. ITEMS FACT. :</span>
            <span>{{ ticket.itemsCount }}</span>
          </div>
          <div class="ticket-footer-line">
            <span>USUARIO :</span>
            <span>{{ ticket.seller }}</span>
          </div>
          <div class="ticket-footer-line">
            <span>FECHA VENCIMIENTO :</span>
            <span>{{ ticket.dateFormatted }}</span>
          </div>
        </div>
      </section>

      <footer class="ticket-legal">
        <div>FRM-POS-001</div>
        <div>Impreso desde MiStock LBP</div>
      </footer>
    </article>
  </div>
</template>

<script>
import {
  loadPosTicketData,
  formatTicketMoneyWithSymbol,
  buildTicketFromRemission,
  mergeTicketCompany,
  resolveCompanyLogoUrl,
} from "@/utils/posTicketPrint";
import remissionsService from "@/services/remissions.service";
import settingsService from "@/services/settings.service";

const body = document.getElementsByTagName("body")[0];

export default {
  name: "PosTicket",
  data() {
    return {
      ticket: null,
      loading: true,
    };
  },
  computed: {
    companyLine() {
      const name = this.ticket?.company?.company_name || "MI STOCK";
      return String(name).toUpperCase();
    },
    customerName() {
      return String(this.ticket?.client?.name || "CONSUMIDOR FINAL").toUpperCase();
    },
    documentLine() {
      const type = this.ticket?.client?.document_type || "CC";
      const doc = this.ticket?.client?.document || "—";
      return `${type} No. : ${doc}`;
    },
    logoUrl() {
      return resolveCompanyLogoUrl(this.ticket?.company);
    },
  },
  methods: {
    money(value) {
      return formatTicketMoneyWithSymbol(value);
    },
    formatQty(qty) {
      const n = Number(qty) || 0;
      return Number.isInteger(n) ? String(n) : n.toLocaleString("es-CO", { maximumFractionDigits: 2 });
    },
    itemLabel(item) {
      const name = String(item.name || "").toUpperCase();
      const suffix = item.ref ? ` (${item.ref})` : " (Und)";
      return name + suffix;
    },
    printTicket() {
      window.print();
    },
    closeTicket() {
      window.close();
      if (!window.closed) {
        this.$router.push({ name: "POS" });
      }
    },
    async fetchCompanySettings() {
      try {
        const settingsRes = await settingsService.get();
        return settingsRes.data || {};
      } catch (e) {
        console.error("No se pudo cargar configuración:", e);
        return {};
      }
    },
    async loadTicket() {
      this.loading = true;
      const remissionId = this.$route.query.id;

      try {
        const company = await this.fetchCompanySettings();
        const cached = loadPosTicketData(remissionId || undefined);

        if (cached) {
          this.ticket = mergeTicketCompany(cached, company);
          return;
        }

        if (!remissionId) {
          this.ticket = null;
          return;
        }

        const remRes = await remissionsService.getData(remissionId);
        const remission = remRes.data;
        const details = remission.details || [];

        this.ticket = buildTicketFromRemission(remission, details, company);
      } catch (e) {
        console.error("Error al cargar ticket:", e);
        this.ticket = null;
      } finally {
        this.loading = false;
      }
    },
  },
  async mounted() {
    this.$store.state.hideConfigButton = true;
    this.$store.state.showSidenav = false;
    this.$store.state.showNavbar = false;
    this.$store.state.showFooter = false;
    body.classList.remove("g-sidenav-show");

    await this.loadTicket();
  },
  beforeUnmount() {
    this.$store.state.hideConfigButton = false;
    this.$store.state.showSidenav = true;
    this.$store.state.showNavbar = true;
    this.$store.state.showFooter = true;
    body.classList.add("g-sidenav-show");
  },
};
</script>

<style scoped>
.pos-ticket-page {
  min-height: 100vh;
  background: #e9ecef;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.pos-ticket-toolbar {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  width: 80mm;
  max-width: 100%;
  justify-content: center;
}

.btn-ticket {
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  color: #fff;
}

.btn-print {
  background: #82d616;
}

.btn-close-tab {
  background: #67748e;
}

.pos-ticket-empty {
  text-align: center;
  padding: 2rem;
}

.ticket {
  width: 80mm;
  max-width: 100%;
  background: #fff;
  color: #000;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 14px;
  line-height: 1.35;
  padding: 3mm 2mm;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
}

.ticket-logo-wrap {
  text-align: center;
  margin-bottom: 0.5rem;
}

.ticket-logo {
  max-width: 56mm;
  max-height: 20mm;
  width: auto;
  height: auto;
  object-fit: contain;
}

.ticket-header {
  text-align: center;
  margin-bottom: 0.5rem;
}

.ticket-company {
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 0.25rem;
  line-height: 1.25;
  text-transform: uppercase;
}

.ticket-sub {
  margin: 0;
  font-size: 14px;
}

.ticket-sep {
  text-align: center;
  letter-spacing: 0;
  margin: 0.35rem 0;
  font-size: 14px;
  overflow: hidden;
  white-space: nowrap;
}

.ticket-doc {
  margin: 0.25rem 0 0.5rem;
  font-size: 14px;
}

.ticket-doc-title {
  margin-bottom: 0.15rem;
}

.ticket-doc-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
}

.ticket-doc-number {
  flex: 1;
  min-width: 0;
}

.ticket-doc-date {
  white-space: nowrap;
  flex-shrink: 0;
}

.ticket-customer {
  margin-bottom: 0.5rem;
  font-size: 14px;
  line-height: 1.35;
}

.ticket-customer-name {
  font-weight: 700;
  text-transform: uppercase;
}

.ticket-customer-doc {
  margin-top: 0.1rem;
}

.ticket-items {
  margin-bottom: 0.35rem;
}

.ticket-items-head,
.ticket-item-row {
  display: grid;
  grid-template-columns: 8% 12% 1fr 34%;
  column-gap: 2px;
  align-items: start;
  font-size: 14px;
}

.ticket-items-head {
  font-weight: 700;
  margin-bottom: 0.1rem;
}

.ticket-item-row {
  margin-bottom: 0.45rem;
  font-size: 14px;
}

.th-num,
.th-qty,
.td-num,
.td-qty {
  text-align: center;
  padding-top: 1px;
}

.th-desc,
.th-total {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.th-desc {
  align-items: center;
  text-align: center;
}

.th-total {
  align-items: flex-end;
  text-align: right;
}

.th-sub {
  font-weight: 400;
  font-size: 12px;
  margin-top: 1px;
}

.td-desc {
  word-break: break-word;
  overflow-wrap: anywhere;
  padding-right: 4px;
  line-height: 1.25;
  text-transform: uppercase;
}

.td-total {
  text-align: right;
  white-space: nowrap;
  padding-top: 1px;
  font-size: 14px;
}

.ticket-totals {
  margin: 0.6rem 0 0.5rem;
  font-size: 14px;
  display: flex;
  justify-content: flex-end;
}

.ticket-totals-block {
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.ticket-total-line {
  display: grid;
  grid-template-columns: auto auto;
  gap: 0.2rem 0.35rem;
  justify-content: end;
  margin-bottom: 0.12rem;
  width: fit-content;
  max-width: 100%;
}

.ticket-total-label {
  text-align: right;
  white-space: nowrap;
}

.ticket-total-value {
  text-align: right;
  white-space: nowrap;
  min-width: 4.5rem;
}

.ticket-total-main {
  font-weight: 700;
}

.ticket-total-main .ticket-total-label,
.ticket-total-main .ticket-total-value {
  font-weight: 700;
}

.ticket-payment {
  margin: 0.5rem 0;
  font-size: 14px;
}

.ticket-payment-head,
.ticket-payment-row {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
}

.ticket-payment-head {
  font-weight: 700;
}

.ticket-footer {
  margin-top: 0.75rem;
  font-size: 14px;
}

.ticket-seller {
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.ticket-footer-block {
  text-align: right;
}

.ticket-footer-line {
  display: flex;
  justify-content: flex-end;
  gap: 0.35rem;
  margin-bottom: 0.1rem;
}

.ticket-legal {
  margin-top: 1rem;
  font-size: 14px;
  line-height: 1.35;
  color: #333;
}

@media print {
  @page {
    size: 80mm auto;
    margin: 2mm;
  }

  .no-print {
    display: none !important;
  }

  .pos-ticket-page {
    background: #fff;
    padding: 0;
    min-height: auto;
  }

  .ticket {
    box-shadow: none;
    width: 80mm;
    max-width: 80mm;
    margin: 0 auto;
    font-size: 14px;
  }
}
</style>
